import type {
  ConflictPolicyName,
  EnqueueInput,
  OutboxOperation,
  SyncQueue,
} from "@norkron/web-sync-queue";
import type {
  CommentCreate,
  CommentStatus,
  CommentVisibility,
  ReplyCreate,
} from "../api/types";
import { mintTempId } from "./tempIds";

/**
 * The mutation-routing layer (design §4.1). Every state-changing `api.*` write
 * is captured as an outbox entry through ONE `enqueueMutation` helper instead of
 * a raw network call, so a write:
 *   1. is durably recorded BEFORE any network (queued while offline),
 *   2. drains in FIFO order on reconnect through `/api/sync/batch`,
 *   3. is applied optimistically via the overlay (`overlay.ts`),
 *   4. leaves the pending set the instant the server confirms (`pruneSucceeded`).
 *
 * The outbox `table`/`operation`/`payload` map is the wire contract the backend
 * `POST /api/sync/batch` terminal dispatches on. `baseVersion` carries the local
 * row's optimistic-concurrency version so the server-side conflict resolver can
 * reconcile a stale write (design §6C).
 */

/** The logical outbox tables the batch terminal dispatches on. */
export const OutboxTable = {
  comments: "comments",
  comment_status: "comment_status",
  comment_visibility: "comment_visibility",
  notes: "notes",
  proposals: "proposals",
  proposal_decision: "proposal_decision",
  governance: "governance",
  settings: "settings",
  membership: "membership",
  folders: "folders",
  versions: "versions",
  notifications: "notifications",
  ticket_status: "ticket_status",
  reactions: "reactions",
} as const;

export type OutboxTableName = (typeof OutboxTable)[keyof typeof OutboxTable];

/**
 * Per-table conflict policy (design §4.4). `server-authoritative` for anything
 * gated (proposals / membership / versions), `lww` for the mergeable middle
 * (status / notes / settings / folders / notifications), `manual` for
 * free-text bodies (comment / governance) that must never be silently clobbered.
 * The server default is `server-authoritative` (deny-by-default); the resolver
 * is a pluggable seam server-side.
 */
export const MUTATION_POLICY: Record<OutboxTableName, ConflictPolicyName> = {
  comments: "manual",
  comment_status: "lww",
  comment_visibility: "lww",
  notes: "lww",
  proposals: "server-authoritative",
  proposal_decision: "server-authoritative",
  governance: "manual",
  settings: "lww",
  membership: "server-authoritative",
  folders: "lww",
  versions: "server-authoritative",
  notifications: "lww",
  ticket_status: "lww",
  // A reaction toggle is an idempotent, order-independent chip flip — last write
  // wins. It does NOT drain through the batch terminal: it replays through the
  // already-idempotent PUT/DELETE reaction endpoint via the reactions outbox's
  // custom transport (see sync/reactions.ts), so this policy is documentary +
  // keeps the write-routing map exhaustive.
  reactions: "lww",
};

/** One row in the write-routing registry (design §4.1 inventory). */
export interface MutationDescriptor {
  table: OutboxTableName;
  operation: OutboxOperation;
  policy: ConflictPolicyName;
}

/** Build a descriptor whose policy is the table's canonical policy. */
function desc(
  table: OutboxTableName,
  operation: OutboxOperation,
): MutationDescriptor {
  return { table, operation, policy: MUTATION_POLICY[table] };
}

/**
 * The declarative routing map: every mutating `api.*` method → its outbox
 * descriptor. `login`/`logout` are intentionally ABSENT — auth must be online, a
 * queued login is meaningless. Read/GET/`exportUrl` are non-mutating and never
 * routed. This is the single source of truth the routing test pins.
 */
export const MUTATION_REGISTRY = {
  createComment: desc(OutboxTable.comments, "create"),
  replyToComment: desc(OutboxTable.comments, "create"),
  setCommentStatus: desc(OutboxTable.comment_status, "update"),
  setCommentVisibility: desc(OutboxTable.comment_visibility, "update"),
  editComment: desc(OutboxTable.comments, "update"),
  createNote: desc(OutboxTable.notes, "create"),
  updateNote: desc(OutboxTable.notes, "update"),
  createProposal: desc(OutboxTable.proposals, "create"),
  acceptProposal: desc(OutboxTable.proposal_decision, "update"),
  rejectProposal: desc(OutboxTable.proposal_decision, "update"),
  saveGovernanceDoc: desc(OutboxTable.governance, "update"),
  updateMe: desc(OutboxTable.settings, "update"),
  updateWorkspaceSettings: desc(OutboxTable.settings, "update"),
  changeMemberRole: desc(OutboxTable.membership, "update"),
  removeMember: desc(OutboxTable.membership, "delete"),
  invitePerson: desc(OutboxTable.membership, "create"),
  createFolder: desc(OutboxTable.folders, "create"),
  renameFolder: desc(OutboxTable.folders, "update"),
  createVersion: desc(OutboxTable.versions, "create"),
  markNotificationRead: desc(OutboxTable.notifications, "update"),
  markAllNotificationsRead: desc(OutboxTable.notifications, "update"),
} as const satisfies Record<string, MutationDescriptor>;

export type WriteKind = keyof typeof MUTATION_REGISTRY;

/** Options for a raw {@link enqueueMutation}. */
export interface EnqueueMutationOpts {
  /** The local row's optimistic-concurrency version at enqueue time. */
  baseVersion?: number | null;
  /** A stable idempotency key (only for a deliberate retry of the same click). */
  clientEventUuid?: string;
}

/**
 * Route ONE write through the outbox. Returns after the durable append (no
 * network on the hot path). The engine mints the UUID v7 idempotency key +
 * drains on its own schedule.
 */
export function enqueueMutation(
  queue: SyncQueue,
  table: OutboxTableName,
  operation: OutboxOperation,
  payload: unknown,
  opts: EnqueueMutationOpts = {},
): Promise<number> {
  const input: EnqueueInput = {
    table,
    operation,
    payload,
    baseVersion: opts.baseVersion ?? null,
    clientEventUuid: opts.clientEventUuid,
  };
  return queue.enqueue(input);
}

// ── Comment-domain typed helpers (the Wave-2 wired backend path) ─────────────

/** The wire payload for a queued comment create (carries its target doc id). */
export interface CreateCommentPayload extends CommentCreate {
  document_id: number;
  /** Client temp id — the optimistic parent's key + the confirm correlation. */
  __tempId: string;
}

/**
 * Queue a comment create. Mints a temp id so an offline reply can reference this
 * not-yet-persisted root (design §5.3); the temp id is also the overlay key for
 * the optimistic comment. Returns `{ outboxId, tempId }`.
 */
export async function enqueueCreateComment(
  queue: SyncQueue,
  documentId: number,
  body: CommentCreate,
): Promise<{ outboxId: number; tempId: string }> {
  const tempId = mintTempId();
  const payload: CreateCommentPayload = {
    ...body,
    document_id: documentId,
    __tempId: tempId,
  };
  const outboxId = await enqueueMutation(
    queue,
    OutboxTable.comments,
    "create",
    payload,
  );
  return { outboxId, tempId };
}

/** The wire payload for a queued reply (parent may be a temp id when offline). */
export interface ReplyPayload extends ReplyCreate {
  /** The document the reply's thread lives on (so the overlay can place it). */
  document_id: number;
  /** Real parent id, or a `tmp-…` id resolved by the serializer at send time. */
  parent_id: number | string;
  __tempId: string;
}

/** Queue a reply to `parentId` (a real id, or a temp id for an offline parent). */
export async function enqueueReply(
  queue: SyncQueue,
  documentId: number,
  parentId: number | string,
  body: ReplyCreate,
): Promise<{ outboxId: number; tempId: string }> {
  const tempId = mintTempId();
  const payload: ReplyPayload = {
    ...body,
    document_id: documentId,
    parent_id: parentId,
    __tempId: tempId,
  };
  const outboxId = await enqueueMutation(
    queue,
    OutboxTable.comments,
    "create",
    payload,
  );
  return { outboxId, tempId };
}

/** The wire payload for a queued status change. */
export interface StatusPayload {
  comment_id: number;
  status: CommentStatus;
}

/** Queue a comment status change (the mergeable middle; `lww`). */
export function enqueueSetStatus(
  queue: SyncQueue,
  commentId: number,
  status: CommentStatus,
  baseVersion?: number | null,
): Promise<number> {
  const payload: StatusPayload = { comment_id: commentId, status };
  return enqueueMutation(
    queue,
    OutboxTable.comment_status,
    "update",
    payload,
    { baseVersion },
  );
}

/** The wire payload for a queued visibility change. */
export interface VisibilityPayload {
  comment_id: number;
  visibility: CommentVisibility;
  shared_with?: number[];
}

/** Queue a comment visibility change (the mergeable middle; `lww`). */
export function enqueueSetVisibility(
  queue: SyncQueue,
  commentId: number,
  visibility: CommentVisibility,
  sharedWith: number[] | undefined,
  baseVersion?: number | null,
): Promise<number> {
  const payload: VisibilityPayload = {
    comment_id: commentId,
    visibility,
    shared_with: sharedWith,
  };
  return enqueueMutation(
    queue,
    OutboxTable.comment_visibility,
    "update",
    payload,
    { baseVersion },
  );
}

/** The wire payload for a queued reaction toggle (replayed via PUT/DELETE). */
export interface ReactionTogglePayload {
  comment_id: number;
  emoji: string;
  /** `true` ⇒ react (PUT), `false` ⇒ un-react (DELETE). */
  add: boolean;
}

/**
 * A STABLE idempotency key for one reaction toggle: `comment + emoji + user +
 * direction`. Re-enqueuing the same toggle (a double-tap while offline) collapses
 * to the same key, and the PUT/DELETE endpoint is itself idempotent, so a replay
 * is a guaranteed no-op. The opposite direction has a distinct key so a react →
 * un-react pair both survive as two ordered entries.
 */
export function reactionToggleKey(
  userId: number,
  commentId: number,
  emoji: string,
  add: boolean,
): string {
  return `reaction:${userId}:${commentId}:${emoji}:${add ? "add" : "remove"}`;
}

/**
 * Queue a reaction toggle. `update` covers both directions (the `add` flag in the
 * payload selects PUT vs DELETE at replay time); the `lww` policy + idempotency
 * key make repeats safe.
 */
export function enqueueToggleReaction(
  queue: SyncQueue,
  userId: number,
  commentId: number,
  emoji: string,
  add: boolean,
): Promise<number> {
  const payload: ReactionTogglePayload = { comment_id: commentId, emoji, add };
  return enqueueMutation(queue, OutboxTable.reactions, "update", payload, {
    clientEventUuid: reactionToggleKey(userId, commentId, emoji, add),
  });
}
